import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import chatService from "../../services/chatService";
import { formatChatMessagePreview } from "../../utils/chatMessagePreview";

/** Normalize conversation id from API/socket (number vs string). */
const sameConversationId = (a, b) =>
  a != null && b != null && String(a) === String(b);

/** Sidebar order: latest activity first */
const sortConversationsByRecent = (list) =>
  [...(list || [])].sort((a, b) => {
    const tb = new Date(b.time || 0).getTime();
    const ta = new Date(a.time || 0).getTime();
    const nb = Number.isNaN(tb) ? 0 : tb;
    const na = Number.isNaN(ta) ? 0 : ta;
    return nb - na;
  });

/** Insert sidebar row when socket message arrives for a conversation not yet loaded (e.g. first support message). */
function ensureConversationInList(state, msg) {
  let convIndex = state.conversations.findIndex((c) => sameConversationId(c.id, msg.conversationId));
  if (convIndex !== -1) return convIndex;

  const isSupport = msg.isSupportRoom === true;
  const previewName = isSupport && msg.sender
    ? `Support: ${msg.sender}`
    : msg.sender || "New message";

  const stub = {
    id: msg.conversationId,
    type: isSupport ? "group" : "individual",
    name: previewName,
    isSupportRoom: isSupport,
    lastMessage: formatChatMessagePreview(msg.content || ""),
    time: msg.time || msg.createdAt || new Date().toISOString(),
    unread: 0,
    members: isSupport ? 2 : undefined,
  };
  if (!sameConversationId(state.activeConversation, msg.conversationId) && !msg.isOwn) {
    stub.unread = 1;
  }
  state.conversations.unshift(stub);
  return 0;
}

function updateConversationPreview(state, msg) {
  const convIndex = ensureConversationInList(state, msg);
  state.conversations[convIndex].lastMessage = formatChatMessagePreview(msg.content);
  state.conversations[convIndex].time = msg.time || msg.createdAt;
  if (!sameConversationId(state.activeConversation, msg.conversationId) && !msg.isOwn) {
    state.conversations[convIndex].unread = (state.conversations[convIndex].unread || 0) + 1;
  }
  const [conv] = state.conversations.splice(convIndex, 1);
  state.conversations.unshift(conv);
}

function reconcileMessageIntoState(state, incoming, clientMessageId) {
  const msg = { ...incoming, status: incoming.status || "sent" };
  const inActive =
    state.activeConversation != null &&
    sameConversationId(msg.conversationId, state.activeConversation);

  if (inActive) {
    if (clientMessageId) {
      const idx = state.messages.findIndex((m) => m.clientMessageId === clientMessageId);
      if (idx !== -1) {
        state.messages[idx] = {
          ...state.messages[idx],
          ...msg,
          id: msg.id,
          isOwn: msg.isOwn ?? state.messages[idx].isOwn,
          clientMessageId: undefined,
          status: "sent",
        };
        updateConversationPreview(state, { ...msg, isOwn: msg.isOwn ?? state.messages[idx].isOwn });
        return;
      }
    }
    if (msg.isOwn) {
      const optIdx = state.messages.findIndex(
        (m) =>
          m.status === "sending" &&
          sameConversationId(m.conversationId, msg.conversationId) &&
          String(m.content || "") === String(msg.content || "")
      );
      if (optIdx !== -1) {
        state.messages[optIdx] = {
          ...msg,
          isOwn: true,
          status: "sent",
          // Preserve clientMessageId so REST response can reconcile without duplication
          clientMessageId: state.messages[optIdx].clientMessageId,
        };
        updateConversationPreview(state, { ...msg, isOwn: true });
        return;
      }
    }
    const exists = state.messages.some(
      (m) => m.id != null && msg.id != null && m.id === msg.id && m.status !== "sending"
    );
    if (!exists) state.messages.push({ ...msg, isOwn: msg.isOwn ?? false });
  }
  updateConversationPreview(state, msg);
}

export function mapSupportConversationPayload(payload) {
  if (!payload?.id) return null;
  const participants = (payload.participants || [])
    .map((p) => {
      const u = p.user;
      const id = u?.id ?? p.userId;
      const name =
        u?.fullName || `${u?.firstName || ""} ${u?.lastName || ""}`.trim() || "User";
      return {
        id,
        firstName: u?.firstName,
        lastName: u?.lastName,
        fullName: u?.fullName,
        name,
        profileImage: u?.profileImage,
        role: u?.role,
      };
    })
    .filter((x) => x.id != null);
  return {
    id: payload.id,
    type: "group",
    isSupportRoom: true,
    name: "Support team",
    members: participants.length,
    participants,
    lastMessage: payload.lastMessage || "",
    time: payload.time || payload.updatedAt || new Date().toISOString(),
    unread: 0,
  };
}

const initialState = {
  conversations: [],
  activeConversation: null,
  messages: [],
  messagesLoading: false,
  messagesMeta: { total: 0, page: 1, totalPages: 1 },
  groups: [],
  communityGroups: [],
  broadcastGroups: [],
  loading: false,
  error: null,
  onlineUsers: [],
};

// Async Thunks
export const fetchConversations = createAsyncThunk(
  "chat/fetchConversations",
  async (options = {}, { rejectWithValue }) => {
    try {
      const response = await chatService.getConversations();
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch conversations");
    }
  }
);

export const createConversation = createAsyncThunk(
  "chat/createConversation",
  async (participantId, { rejectWithValue }) => {
    try {
      const response = await chatService.createConversation(participantId);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to create conversation");
    }
  }
);

export const ensureSupportConversation = createAsyncThunk(
  "chat/ensureSupportConversation",
  async (_, { rejectWithValue }) => {
    try {
      const response = await chatService.ensureSupportConversation();
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.response?.data?.error || "Could not open support chat"
      );
    }
  }
);

export const fetchMessages = createAsyncThunk(
  "chat/fetchMessages",
  async ({ conversationId, params }, { rejectWithValue }) => {
    if (conversationId == null || !Number.isFinite(Number(conversationId))) {
      return rejectWithValue("Invalid conversation id");
    }
    try {
      const response = await chatService.getMessages(conversationId, params);
      return response.data.data || response.data || response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch messages");
    }
  }
);

export const sendMessage = createAsyncThunk(
  "chat/sendMessage",
  async ({ conversationId, message, clientMessageId }, { rejectWithValue }) => {
    try {
      const response = await chatService.sendMessage(conversationId, message);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to send message");
    }
  }
);

export const deleteMessage = createAsyncThunk(
  "chat/deleteMessage",
  async ({ conversationId, messageId }, { rejectWithValue }) => {
    try {
      await chatService.deleteMessage(conversationId, messageId);
      return messageId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to delete message");
    }
  }
);

export const fetchGroups = createAsyncThunk(
  "chat/fetchGroups",
  async (_, { rejectWithValue }) => {
    try {
      const response = await chatService.getGroups();
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch groups");
    }
  }
);

export const createGroup = createAsyncThunk(
  "chat/createGroup",
  async (groupData, { rejectWithValue }) => {
    try {
      const response = await chatService.createGroup(groupData);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to create group");
    }
  }
);

export const fetchGroupMessages = createAsyncThunk(
  "chat/fetchGroupMessages",
  async ({ groupId, params }, { rejectWithValue }) => {
    try {
      const response = await chatService.getGroupMessages(groupId, params);
      return response.data.data || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch group messages");
    }
  }
);

export const sendGroupMessage = createAsyncThunk(
  "chat/sendGroupMessage",
  async ({ groupId, message, clientMessageId }, { rejectWithValue }) => {
    try {
      const response = await chatService.sendGroupMessage(groupId, message);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to send group message");
    }
  }
);

export const addGroupMembers = createAsyncThunk(
  "chat/addGroupMembers",
  async ({ groupId, memberIds }, { rejectWithValue }) => {
    try {
      await chatService.addGroupMembers(groupId, memberIds);
      return { groupId, memberIds };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to add members");
    }
  }
);

export const removeGroupMember = createAsyncThunk(
  "chat/removeGroupMember",
  async ({ groupId, memberId }, { rejectWithValue }) => {
    try {
      await chatService.removeGroupMember(groupId, memberId);
      return { groupId, memberId };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to remove member");
    }
  }
);

export const transferGroupAdmin = createAsyncThunk(
  "chat/transferGroupAdmin",
  async ({ groupId, newAdminUserId }, { rejectWithValue }) => {
    try {
      const res = await chatService.transferGroupAdmin(groupId, newAdminUserId);
      return res.data?.data || { groupId, adminUserId: newAdminUserId };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to transfer admin");
    }
  }
);

export const promoteGroupMemberAdmin = createAsyncThunk(
  "chat/promoteGroupMemberAdmin",
  async ({ groupId, memberId }, { rejectWithValue }) => {
    try {
      const res = await chatService.promoteGroupMemberAdmin(groupId, memberId);
      return res.data?.data || { groupId, memberId };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to make admin");
    }
  }
);

export const revokeGroupMemberAdmin = createAsyncThunk(
  "chat/revokeGroupMemberAdmin",
  async ({ groupId, memberId }, { rejectWithValue }) => {
    try {
      const res = await chatService.revokeGroupMemberAdmin(groupId, memberId);
      return res.data?.data || { groupId, memberId };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to remove admin");
    }
  }
);

export const fetchCommunityGroups = createAsyncThunk(
  "chat/fetchCommunityGroups",
  async (_, { rejectWithValue }) => {
    try {
      const response = await chatService.getCommunityGroups();
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch community groups");
    }
  }
);

export const createCommunityGroup = createAsyncThunk(
  "chat/createCommunityGroup",
  async (groupData, { rejectWithValue }) => {
    try {
      const response = await chatService.createCommunityGroup(groupData);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to create community group");
    }
  }
);

export const joinCommunityGroup = createAsyncThunk(
  "chat/joinCommunityGroup",
  async (groupId, { rejectWithValue }) => {
    try {
      await chatService.joinCommunityGroup(groupId);
      return groupId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to join community group");
    }
  }
);

export const leaveCommunityGroup = createAsyncThunk(
  "chat/leaveCommunityGroup",
  async (groupId, { rejectWithValue }) => {
    try {
      await chatService.leaveCommunityGroup(groupId);
      return groupId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to leave community group");
    }
  }
);

export const fetchBroadcastGroups = createAsyncThunk(
  "chat/fetchBroadcastGroups",
  async (_, { rejectWithValue }) => {
    try {
      const response = await chatService.getBroadcastGroups();
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch broadcast groups");
    }
  }
);

export const createBroadcastGroup = createAsyncThunk(
  "chat/createBroadcastGroup",
  async (groupData, { rejectWithValue }) => {
    try {
      const response = await chatService.createBroadcastGroup(groupData);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to create broadcast group");
    }
  }
);

const chatSlice = createSlice({
  name: "chat",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setActiveConversation: (state, action) => {
      state.activeConversation = action.payload;
    },
    addOptimisticMessage: (state, action) => {
      const msg = action.payload;
      if (
        state.activeConversation != null &&
        sameConversationId(msg.conversationId, state.activeConversation)
      ) {
        state.messages.push(msg);
      }
      updateConversationPreview(state, msg);
    },
    addMessage: (state, action) => {
      const msg = action.payload;
      reconcileMessageIntoState(state, msg, action.payload?.clientMessageId);
    },
    markMessageFailed: (state, action) => {
      const clientMessageId = action.payload;
      const idx = state.messages.findIndex((m) => m.clientMessageId === clientMessageId);
      if (idx !== -1) state.messages[idx].status = "failed";
    },
    setOnlineUsers: (state, action) => {
      state.onlineUsers = action.payload;
    },
    userOnline: (state, action) => {
      if (!state.onlineUsers.includes(action.payload)) {
        state.onlineUsers.push(action.payload);
      }
    },
    userOffline: (state, action) => {
      state.onlineUsers = state.onlineUsers.filter((id) => id !== action.payload);
    },
    clearMessages: (state) => {
      state.messages = [];
      state.messagesMeta = { total: 0, page: 1, totalPages: 1 };
    },
    clearConversationUnread: (state, action) => {
      const conversationId = action.payload;
      const convIndex = state.conversations.findIndex((c) =>
        sameConversationId(c.id, conversationId)
      );
      if (convIndex !== -1) {
        state.conversations[convIndex].unread = 0;
      }
    },
    upsertConversationPreview: (state, action) => {
      const conv = action.payload;
      if (!conv?.id) return;
      const idx = state.conversations.findIndex((c) => sameConversationId(c.id, conv.id));
      if (idx === -1) {
        state.conversations.unshift({
          unread: 0,
          lastMessage: "",
          time: conv.time || conv.updatedAt || new Date().toISOString(),
          ...conv,
        });
      } else {
        state.conversations[idx] = { ...state.conversations[idx], ...conv };
      }
      state.conversations = sortConversationsByRecent(state.conversations);
    },
    reactivateGroupMembers: (state, action) => {
      const { conversationId, memberIds, member } = action.payload || {};
      if (!conversationId || !Array.isArray(memberIds) || memberIds.length === 0) return;
      const conv = state.conversations.find((c) => sameConversationId(c.id, conversationId));
      if (!conv) return;
      if (!Array.isArray(conv.participants)) conv.participants = [];
      memberIds.forEach((memberId) => {
        const numericId = Number(memberId);
        if (!conv.participants.some((p) => Number(p.id) === numericId)) {
          if (member && Number(member.id) === numericId) {
            conv.participants.push(member);
          } else {
            conv.participants.push({ id: numericId, name: "Member" });
          }
        }
      });
      conv.members = Math.max(conv.participants.length, conv.members || 0);
      conv.isActiveMember = true;
    },
    setConversationMembership: (state, action) => {
      const { conversationId, isActiveMember } = action.payload || {};
      if (conversationId == null) return;
      const conv = state.conversations.find((c) => sameConversationId(c.id, conversationId));
      if (!conv) return;
      conv.isActiveMember = isActiveMember !== false;
      if (isActiveMember === false && Array.isArray(conv.participants)) {
        const currentUserId = (() => {
          try {
            const token = localStorage.getItem("accessToken");
            if (!token) return null;
            const payload = JSON.parse(atob(token.split(".")[1] || "e30="));
            return payload?.id ?? null;
          } catch {
            return null;
          }
        })();
        if (currentUserId != null) {
          conv.participants = conv.participants.filter(
            (p) => Number(p.id) !== Number(currentUserId)
          );
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Conversations
      .addCase(fetchConversations.pending, (state, action) => {
        const arg = action.meta?.arg;
        const silent = typeof arg === "object" && arg?.silent === true;
        if (!silent && state.conversations.length === 0) state.loading = true;
        state.error = null;
      })
      .addCase(ensureSupportConversation.fulfilled, (state, action) => {
        const mapped = mapSupportConversationPayload(action.payload);
        if (!mapped) return;
        const idx = state.conversations.findIndex((c) => sameConversationId(c.id, mapped.id));
        if (idx === -1) state.conversations.unshift(mapped);
        else state.conversations[idx] = { ...state.conversations[idx], ...mapped };
        state.conversations = sortConversationsByRecent(state.conversations);
      })
      .addCase(fetchConversations.fulfilled, (state, action) => {
        state.loading = false;
        state.conversations = sortConversationsByRecent(action.payload || []);
      })
      .addCase(fetchConversations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create Conversation
      .addCase(createConversation.fulfilled, (state, action) => {
        if (action.payload) {
          const exists = state.conversations.find((c) => c.id === action.payload.id);
          if (!exists) state.conversations.unshift(action.payload);
        }
      })
      // Fetch Messages
      .addCase(fetchMessages.pending, (state) => {
        state.messagesLoading = true;
      })
      .addCase(fetchMessages.fulfilled, (state, action) => {
        state.messagesLoading = false;
        const payload = action.payload;
        if (Array.isArray(payload)) {
          state.messages = payload;
          state.messagesMeta = { total: payload.length, page: 1, totalPages: 1 };
        } else if (payload && payload.messages) {
          state.messages = payload.messages;
          state.messagesMeta = {
            total: payload.total || payload.messages.length || 0,
            page: payload.page || 1,
            totalPages: payload.totalPages || 1,
          };
        } else {
          state.messages = [];
        }
      })
      .addCase(fetchMessages.rejected, (state) => {
        state.messagesLoading = false;
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        if (action.payload) {
          const clientMessageId = action.meta?.arg?.clientMessageId;
          reconcileMessageIntoState(
            state,
            { ...action.payload, isOwn: true },
            clientMessageId
          );
        }
      })
      .addCase(sendMessage.rejected, (state, action) => {
        const clientMessageId = action.meta?.arg?.clientMessageId;
        if (clientMessageId) {
          const idx = state.messages.findIndex((m) => m.clientMessageId === clientMessageId);
          if (idx !== -1) state.messages[idx].status = "failed";
        }
      })
      // Delete Message
      .addCase(deleteMessage.fulfilled, (state, action) => {
        state.messages = state.messages.filter((m) => m.id !== action.payload);
      })
      // Fetch Groups
      .addCase(fetchGroups.fulfilled, (state, action) => {
        state.groups = action.payload || [];
      })
      // Create Group
      .addCase(createGroup.fulfilled, (state, action) => {
        if (action.payload) {
          state.groups.push(action.payload);
          const { id, name, members } = action.payload;
          const exists = state.conversations.some((c) => c.id === id);
          if (!exists) {
            state.conversations.unshift({
              id,
              type: "group",
              name,
              members,
              lastMessage: "",
              time: new Date().toISOString(),
              unread: 0,
            });
          }
        }
      })
      // Fetch Group Messages
      .addCase(fetchGroupMessages.pending, (state) => {
        state.messagesLoading = true;
      })
      .addCase(fetchGroupMessages.fulfilled, (state, action) => {
        state.messagesLoading = false;
        const payload = action.payload;
        if (Array.isArray(payload)) {
          state.messages = payload;
        } else if (payload && payload.messages) {
          state.messages = payload.messages;
        } else {
          state.messages = [];
        }
      })
      .addCase(fetchGroupMessages.rejected, (state) => {
        state.messagesLoading = false;
      })
      .addCase(sendGroupMessage.fulfilled, (state, action) => {
        if (action.payload) {
          const clientMessageId = action.meta?.arg?.clientMessageId;
          reconcileMessageIntoState(
            state,
            { ...action.payload, isOwn: true },
            clientMessageId
          );
        }
      })
      .addCase(sendGroupMessage.rejected, (state, action) => {
        const clientMessageId = action.meta?.arg?.clientMessageId;
        if (clientMessageId) {
          const idx = state.messages.findIndex((m) => m.clientMessageId === clientMessageId);
          if (idx !== -1) state.messages[idx].status = "failed";
        }
      })
      // Add Group Members
      .addCase(addGroupMembers.fulfilled, (state, action) => {
        if (action.payload) {
          const { groupId, memberIds } = action.payload;
          const conv = state.conversations.find((c) => sameConversationId(c.id, groupId));
          if (conv) conv.members = (conv.members || 0) + (memberIds?.length || 0);
        }
      })
      // Remove Group Member
      .addCase(removeGroupMember.fulfilled, (state, action) => {
        if (action.payload) {
          const conv = state.conversations.find((c) => c.id === action.payload.groupId);
          if (conv) conv.members = Math.max(0, (conv.members || 1) - 1);
        }
      })
      // Fetch Community Groups
      .addCase(fetchCommunityGroups.fulfilled, (state, action) => {
        state.communityGroups = action.payload || [];
      })
      // Create Community Group
      .addCase(createCommunityGroup.fulfilled, (state, action) => {
        if (action.payload) state.communityGroups.push(action.payload);
      })
      // Fetch Broadcast Groups
      .addCase(fetchBroadcastGroups.fulfilled, (state, action) => {
        state.broadcastGroups = action.payload || [];
      })
      // Create Broadcast Group
      .addCase(createBroadcastGroup.fulfilled, (state, action) => {
        if (action.payload) state.broadcastGroups.push(action.payload);
      });
  },
});

export const {
  clearError,
  setActiveConversation,
  addOptimisticMessage,
  addMessage,
  markMessageFailed,
  setOnlineUsers,
  userOnline,
  userOffline,
  clearMessages,
  clearConversationUnread,
  upsertConversationPreview,
  reactivateGroupMembers,
  setConversationMembership,
} = chatSlice.actions;

export default chatSlice.reducer;
